
import { useState, useEffect } from 'react';

export interface Message {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
  icon?: string;
}

interface MessageBarProps {
  messages: Message[];
  onRemoveMessage: (id: string) => void;
}

export default function MessageBar({ messages, onRemoveMessage }: MessageBarProps) {
  const [visibleMessages, setVisibleMessages] = useState<Message[]>([]);

  useEffect(() => {
    // 当有新消息时，添加到可见消息列表
    messages.forEach(message => {
      if (!visibleMessages.find(m => m.id === message.id)) {
        setVisibleMessages(prev => [...prev, message]);
        
        // 设置自动消失定时器
        const duration = message.duration || 5000;
        setTimeout(() => {
          handleRemoveMessage(message.id);
        }, duration);
      }
    });
  }, [messages]);

  const handleRemoveMessage = (id: string) => {
    setVisibleMessages(prev => prev.filter(m => m.id !== id));
    onRemoveMessage(id);
  };

  const getMessageStyles = (type: Message['type']) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-500',
          border: 'border-green-600',
          text: 'text-white',
          icon: 'ri-check-line'
        };
      case 'error':
        return {
          bg: 'bg-red-500',
          border: 'border-red-600',
          text: 'text-white',
          icon: 'ri-error-warning-line'
        };
      case 'warning':
        return {
          bg: 'bg-yellow-500',
          border: 'border-yellow-600',
          text: 'text-white',
          icon: 'ri-alert-line'
        };
      case 'info':
        return {
          bg: 'bg-blue-500',
          border: 'border-blue-600',
          text: 'text-white',
          icon: 'ri-information-line'
        };
      default:
        return {
          bg: 'bg-gray-500',
          border: 'border-gray-600',
          text: 'text-white',
          icon: 'ri-notification-line'
        };
    }
  };

  if (visibleMessages.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm w-full">
      {visibleMessages.map((message) => {
        const styles = getMessageStyles(message.type);
        return (
          <div
            key={message.id}
            className={`${styles.bg} ${styles.border} ${styles.text} rounded-lg shadow-lg border animate-in slide-in-from-right-4 duration-300`}
          >
            <div className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className="flex-shrink-0 mt-0.5">
                    <i className={`${message.icon || styles.icon} text-lg`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    {message.title && (
                      <h4 className="font-semibold text-sm mb-1">{message.title}</h4>
                    )}
                    <p className="text-sm leading-relaxed">{message.message}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleRemoveMessage(message.id)}
                  className="flex-shrink-0 ml-2 w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
              
              {/* 进度条 */}
              <div className="mt-3 h-1 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white/60 rounded-full animate-shrink-width"
                  style={{
                    animationDuration: `${message.duration || 5000}ms`,
                    animationTimingFunction: 'linear'
                  }}
                ></div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// 消息管理 Hook
export function useMessageBar() {
  const [messages, setMessages] = useState<Message[]>([]);

  const addMessage = (message: Omit<Message, 'id'>) => {
    const newMessage: Message = {
      ...message,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const removeMessage = (id: string) => {
    setMessages(prev => prev.filter(m => m.id !== id));
  };

  const showSuccess = (message: string, title?: string) => {
    addMessage({ type: 'success', message, title });
  };

  const showError = (message: string, title?: string) => {
    addMessage({ type: 'error', message, title });
  };

  const showWarning = (message: string, title?: string) => {
    addMessage({ type: 'warning', message, title });
  };

  const showInfo = (message: string, title?: string) => {
    addMessage({ type: 'info', message, title });
  };

  return {
    messages,
    addMessage,
    removeMessage,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };
}
